import { barGeometry, SLANT_RATIO } from "../layout/bar_geometry";
import { packLanes } from "../layout/lane_packer";
import { markTurnarounds } from "../layout/turnarounds";
import { applyCuts, barLabel } from "./bar_cuts";
import { escapeHtml, formatDateString, parseLocalDate } from "../utils";

// Narrowest a day column may become before the board scrolls instead. Below
// this a column cannot carry a legible bar, so crushing is worse than
// horizontal scrolling.
const MIN_COL_PX = 64;

// Week view: one row per vehicle, one column per day.
//
// Horizontal axis is DAYS, not minutes — fleetmo bookings are multi-day
// rentals. Drag snaps to whole days and never alters time-of-day; that is
// edited in the form.
//
// Colour identifies the VEHICLE (data-color); booking status is carried by
// texture (data-texture), so a short bar spends no horizontal space saying
// what it is.
export class ResourceTimelineView {
  constructor(scheduler) {
    this.scheduler = scheduler;
    this.element = document.createElement("div");
    this.element.className = "calendar-timeline";
    this._domListeners = [];
    this._rowLayers = new Map(); // resourceId -> HTMLElement
    this._activePointerCleanup = null; // ends an in-flight drag/resize on teardown
  }

  mount(parent) {
    parent.appendChild(this.element);
    this._render();
  }

  setData() {
    this._render();
  }

  destroy() {
    this._tearDownDomListeners();
    this.element.remove();
  }

  // ---- internal ----

  get days() {
    const start = this._rangeStart();
    const count = this.dayCount;
    return Array.from({ length: count }, (_, i) => {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      return d;
    });
  }

  get dayCount() {
    return 7 * this.scheduler.span;
  }

  // Columns stretch to fill the board, so the width of a day is MEASURED, not
  // declared — a fixed width is what left a gutter on wide screens and clipped
  // the last bar. Falls back to the CSS floor before first layout.
  get colWidth() {
    const track = this.element.querySelector(".calendar-timeline__track");
    if (track && track.clientWidth > 0) return track.clientWidth / this.dayCount;
    return MIN_COL_PX;
  }

  get range() {
    return { startDate: formatDateString(this._rangeStart()), dayCount: this.dayCount };
  }

  // Monday-first week containing the scheduler's date.
  _rangeStart() {
    const d = parseLocalDate(this.scheduler.date);
    const dow = (d.getDay() + 6) % 7; // Mon = 0
    d.setDate(d.getDate() - dow);
    return d;
  }

  _render() {
    this._tearDownDomListeners();
    this.element.innerHTML = "";
    this._rowLayers.clear();

    this.element.appendChild(this._buildHeader());
    this.element.appendChild(this._buildBody());
    this._paintEvents();
  }

  _buildHeader() {
    const header = document.createElement("div");
    header.className = "calendar-timeline__header";
    header.style.display = "grid";
    header.style.gridTemplateColumns =
      `var(--calendar-resource-column-width) repeat(${this.dayCount}, minmax(${MIN_COL_PX}px, 1fr))`;

    const corner = document.createElement("div");
    corner.className = "calendar-timeline__corner";
    corner.textContent = "Vehicle";
    header.appendChild(corner);

    const todayString = formatDateString(new Date());
    this.days.forEach((day) => {
      const cell = document.createElement("div");
      cell.className = "calendar-timeline__day-head";
      if (formatDateString(day) === todayString) cell.dataset.today = "true";
      cell.innerHTML = `
        <div class="calendar-timeline__dow">${day.toLocaleDateString(undefined, { weekday: "short" })}</div>
        <div class="calendar-timeline__dom">${day.getDate()}</div>
      `;
      header.appendChild(cell);
    });

    return header;
  }

  _buildBody() {
    const body = document.createElement("div");
    body.className = "calendar-timeline__body";

    this.scheduler.resources.forEach((resource) => {
      body.appendChild(this._buildRow(resource));
    });

    if (this.scheduler.resources.length === 0) {
      const empty = document.createElement("div");
      empty.className = "calendar-timeline__empty";
      empty.textContent = "Add vehicles to see the calendar.";
      body.appendChild(empty);
    }

    return body;
  }

  _buildRow(resource) {
    const row = document.createElement("div");
    row.className = "calendar-timeline__row";
    row.dataset.resourceId = String(resource.id);
    row.style.display = "grid";
    row.style.gridTemplateColumns = `var(--calendar-resource-column-width) 1fr`;

    const label = document.createElement("div");
    label.className = "calendar-timeline__resource";
    // The swatch does the recognising and the plate disambiguates, so the name
    // can truncate to one line. It used to wrap to two and set the row height
    // on its own, which is why compacting the bars alone bought so little.
    //
    // Because every row shows its own swatch, this column IS the colour
    // legend — the board needs no separate one.
    label.innerHTML = `
      <span class="calendar-timeline__resource-line">
        <span class="calendar-timeline__swatch" data-color="${escapeHtml(resource.color || "indigo")}"></span>
        <span class="calendar-timeline__resource-name">${escapeHtml(resource.name)}</span>
      </span>
      ${resource.subtitle ? `<div class="calendar-timeline__resource-sub">${escapeHtml(resource.subtitle)}</div>` : ""}
    `;
    row.appendChild(label);

    const track = document.createElement("div");
    track.className = "calendar-timeline__track";
    track.style.position = "relative";
    track.style.minHeight = "var(--calendar-row-height)";

    // Clickable empty day cells (drag across them to create).
    const cells = document.createElement("div");
    cells.style.display = "grid";
    cells.style.gridTemplateColumns = `repeat(${this.dayCount}, minmax(${MIN_COL_PX}px, 1fr))`;
    cells.style.height = "100%";

    this.days.forEach((day) => {
      const cell = document.createElement("div");
      cell.className = "calendar-timeline__cell";
      cell.dataset.date = formatDateString(day);
      cell.dataset.resourceId = String(resource.id);
      cells.appendChild(cell);
    });
    track.appendChild(cells);

    // Absolute layer the bars live in.
    const layer = document.createElement("div");
    layer.className = "calendar-timeline__layer";
    layer.style.position = "absolute";
    layer.style.inset = "0";
    layer.style.pointerEvents = "none";
    track.appendChild(layer);
    this._rowLayers.set(String(resource.id), layer);

    this._enableRangeSelect(cells, resource);

    row.appendChild(track);
    return row;
  }

  _paintEvents() {
    this._rowLayers.forEach((layer) => (layer.innerHTML = ""));

    const byResource = new Map();
    this.scheduler.events.forEach((item) => {
      const key = String(item.resourceId);
      if (!byResource.has(key)) byResource.set(key, []);
      byResource.get(key).push(item);
    });

    byResource.forEach((items, resourceId) => {
      const layer = this._rowLayers.get(resourceId);
      if (!layer) return;

      // A handover shares its day rather than stacking: the outgoing bar is
      // shortened so the two tile the shared column. Lanes are reserved for
      // genuine double-bookings, which markTurnarounds deliberately excludes.
      const turnarounds = markTurnarounds(items);
      // Same flags drive both: the geometry shortens a flagged bar, and lane
      // packing lets that one pair — and only that pair — share a lane.
      const { lanes } = packLanes(items, { maxLanes: 3, turnarounds });

      lanes.forEach((lane, laneIdx) => {
        lane.forEach((item) => {
          const geo = barGeometry(item, this.range, this.colWidth, turnarounds.get(item.id));
          if (!geo) return;
          layer.appendChild(this._buildBar(item, geo, laneIdx, lanes.length));
        });
      });
    });
  }

  _buildBar(item, geo, laneIdx, laneCount) {
    const node = document.createElement("div");
    node.className = "calendar-bar";
    node.dataset.eventId = String(item.id);
    node.dataset.kind = item.kind;
    // Percentages, not pixels: the columns are fluid, so a bar positioned in
    // measured pixels would drift the moment the window resized.
    const trackWidth = this.colWidth * this.dayCount;
    node.style.left = `${(geo.left / trackWidth) * 100}%`;
    node.style.width = `${(geo.width / trackWidth) * 100}%`;
    node.style.pointerEvents = "auto";

    // Colour is the vehicle, texture is the status. Both are data attributes
    // so the CSS owns the palette and the JS never names a hex value.
    node.dataset.color = item.color || "indigo";
    if (item.status === "out") node.dataset.texture = "striped";

    const laneHeight = 100 / laneCount;
    node.style.top = `${laneIdx * laneHeight}%`;
    node.style.height = `calc(${laneHeight}% - 4px)`;

    if (geo.continuesLeft) node.dataset.continuesLeft = "true";
    if (geo.continuesRight) node.dataset.continuesRight = "true";
    if (!item.draggable) node.dataset.locked = "true";

    // The slant is unconditional — every bar is a parallelogram — so this is no
    // longer a flag, just geometry.
    applyCuts(node, geo);

    // One line: the abbreviated customer name and nothing else. Status left
    // with the text when texture took over carrying it.
    // colWidth is in pixels here, so the threshold is too.
    const label = barLabel(item.title, geo, 52);
    node.title = item.title;
    node.innerHTML =
      `<span class="calendar-bar__title"${label.abbreviated ? " data-abbreviated" : ""}>` +
      `${escapeHtml(label.text)}</span>`;

    this._on(node, "click", (e) => this._handleEventClick(e, item));

    if (item.draggable) {
      this._enableDrag(node, item);
      this._buildResizeHandles(node, item).forEach((h) => node.appendChild(h));
    }

    return node;
  }

  // One handle per end. The START one is what lets an operator pull a pickup
  // earlier without opening the form; before this the board could only ever
  // change the return date.
  _buildResizeHandles(node, item) {
    return [ "start", "end" ].map((edge) => {
      const handle = document.createElement("span");
      handle.className = `calendar-bar__resize calendar-bar__resize--${edge}`;
      handle.dataset.edge = edge;
      this._enableResize(handle, node, item, edge);
      return handle;
    });
  }

  // ---- interactions ----

  // Drag across empty cells on one row to propose a new booking.
  _enableRangeSelect(cells, resource) {
    let anchor = null;

    this._on(cells, "mousedown", (e) => {
      const cell = e.target.closest("[data-date]");
      if (!cell) return;
      anchor = cell.dataset.date;
    });

    this._on(cells, "mouseup", (e) => {
      if (!anchor) return;
      const cell = e.target.closest("[data-date]");
      const focus = cell ? cell.dataset.date : anchor;
      const [startDate, endDate] = [anchor, focus].sort();
      anchor = null;

      this.scheduler._emit("range-select", {
        resourceId: resource.id,
        startDate,
        endDate
      });
    });
  }

  // Bars are laid out in PERCENTAGES of the track (see _buildBar), so a drag
  // preview has to convert both ways rather than parsing the style string as
  // pixels — doing that read `21.4%` as `21.4px` and threw the bar at the left
  // edge the moment the pointer moved.
  get _trackWidth() {
    const track = this.element.querySelector(".calendar-timeline__track");
    const measured = track ? track.clientWidth : 0;
    // Before first layout (and under a headless DOM) clientWidth is 0; fall
    // back to the same floor colWidth uses so the arithmetic stays finite.
    return measured > 0 ? measured : this.colWidth * this.dayCount;
  }

  _pctToPx(pct) { return (pct / 100) * this._trackWidth; }
  _pxToPct(px) { return (px / this._trackWidth) * 100; }

  // Day-snapped move. Horizontal drag shifts dates; vertical drag re-parents
  // to another vehicle row. Time-of-day is preserved — see spec decision 2.
  _enableDrag(node, item) {
    this._on(node, "mousedown", (startEvent) => {
      if (startEvent.target.closest(".calendar-bar__resize")) return;
      startEvent.preventDefault();

      const state = {
        startX: startEvent.clientX,
        startY: startEvent.clientY,
        originLeft: this._pctToPx(parseFloat(node.style.left)),
        dayDelta: 0,
        resourceId: item.resourceId
      };

      const onMove = (e) => {
        state.dayDelta = Math.round((e.clientX - state.startX) / this.colWidth);

        node.style.left =
          `${this._pxToPct(state.originLeft + state.dayDelta * this.colWidth)}%`;

        // Find the row under the pointer for cross-vehicle moves.
        node.style.pointerEvents = "none";
        const under = document.elementFromPoint(e.clientX, e.clientY);
        node.style.pointerEvents = "auto";
        const row = under && under.closest("[data-resource-id]");
        if (row) state.resourceId = row.dataset.resourceId;

        this.scheduler._emit("event-drag", {
          id: item.id,
          pending: this._shift(item, state.dayDelta, state.resourceId)
        });
      };

      const onUp = () => {
        this._activePointerCleanup = null;
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);

        // Gate on whether anything actually CHANGED, not on pointer
        // movement. `moved` only tracked horizontal delta, so a straight
        // vertical drag to another vehicle row with the same dates — a
        // real re-parent — was discarded here and fell through to a click.
        // A sub-column wobble that snaps back to the same day and row must
        // still no-op (M7): it would otherwise emit a move with identical
        // values, PATCHing nothing but the lock_version.
        const changed =
          state.dayDelta !== 0 || String(state.resourceId) !== String(item.resourceId);
        if (!changed) {
          // Nothing to commit, but a preview may have armed a pending
          // Confirm box for this gesture — tell the host to tear it down.
          this.scheduler._emit("drag-cancel", { id: item.id });
          return;
        }

        // Suppress the click that fires after mouseup post-drag. Registered
        // on `document`, not on `node` — see `_suppressNextClick`.
        this._suppressNextClick();

        this.scheduler._emit("event-move", {
          id: item.id,
          kind: item.kind,
          original: { start: item.start, end: item.end, resourceId: item.resourceId },
          next: this._shift(item, state.dayDelta, state.resourceId)
        });
      };

      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);

      // Lets teardown (destroy / re-render mid-drag) end this session even
      // though these listeners live on `document`, outside `_domListeners`.
      this._activePointerCleanup = () => {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
      };
    });
  }

  // A resize changes the bar's width, and the cuts are fractions OF that width
  // — so left alone, the slant grows with the booking. Recompute them from the
  // fixed pixel slant on every move, so what is drawn mid-drag is what
  // barGeometry will produce on commit.
  //
  // An end that was flat stays flat: a bar running off the visible range is cut
  // by the viewport, not ended, and dragging the other end does not change that.
  _previewCuts(node, widthPx, hadLeft, hadRight) {
    const slant = this.colWidth * SLANT_RATIO;
    applyCuts(node, {
      width: widthPx,
      cutLeft: hadLeft ? slant / widthPx : 0,
      cutRight: hadRight ? slant / widthPx : 0
    });
  }

  // Drag either edge to change the rental's length. The opposite end never
  // moves: a resize changes duration, a whole-bar drag changes when it starts.
  _enableResize(handle, node, item, edge = "end") {
    this._on(handle, "mousedown", (startEvent) => {
      startEvent.preventDefault();
      startEvent.stopPropagation();

      const startX = startEvent.clientX;
      const originLeft = this._pctToPx(parseFloat(node.style.left));
      const originWidth = this._pctToPx(parseFloat(node.style.width));
      const hadLeft = parseFloat(node.style.getPropertyValue("--cut-l")) > 0;
      const hadRight = parseFloat(node.style.getPropertyValue("--cut-r")) > 0;
      let dayDelta = 0;

      const onMove = (e) => {
        const raw = Math.round((e.clientX - startX) / this.colWidth);
        // One day column is the natural floor now that bars cover whole cells:
        // a booking cannot be shorter than the day it occupies. Clamping the
        // DELTA rather than the pixel width keeps the emitted dates honest —
        // clamping only the box would preview a length the payload contradicts.
        const maxShrink = Math.round(originWidth / this.colWidth) - 1;
        dayDelta = edge === "end" ? Math.max(raw, -maxShrink) : Math.min(raw, maxShrink);

        const widthPx = edge === "end"
          ? originWidth + dayDelta * this.colWidth
          : originWidth - dayDelta * this.colWidth;

        if (edge === "end") {
          node.style.width = `${this._pxToPct(widthPx)}%`;
        } else {
          node.style.left = `${this._pxToPct(originLeft + dayDelta * this.colWidth)}%`;
          node.style.width = `${this._pxToPct(widthPx)}%`;
        }
        this._previewCuts(node, widthPx, hadLeft, hadRight);

        this.scheduler._emit("event-drag", {
          id: item.id,
          pending: edge === "end"
            ? { start: item.start, end: this._addDays(item.end, dayDelta), resourceId: item.resourceId }
            : { start: this._addDays(item.start, dayDelta), end: item.end, resourceId: item.resourceId }
        });
      };

      const onUp = () => {
        this._activePointerCleanup = null;
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
        if (dayDelta === 0) {
          // Nothing resized (width already back at originWidth); let the
          // click through, but a preview may have armed a pending Confirm
          // box for this abandoned gesture — tear it down.
          this.scheduler._emit("drag-cancel", { id: item.id });
          return;
        }

        // Same click-after-drag hazard as `_enableDrag` — the pointer can
        // end mouseup over `node` (the handle is its child), which would
        // otherwise fire a spurious event-click right after a resize.
        this._suppressNextClick();

        this.scheduler._emit("event-resize", {
          id: item.id,
          kind: item.kind,
          original: { start: item.start, end: item.end, resourceId: item.resourceId },
          next: edge === "end"
            ? { start: item.start, end: this._addDays(item.end, dayDelta), resourceId: item.resourceId }
            : { start: this._addDays(item.start, dayDelta), end: item.end, resourceId: item.resourceId }
        });
      };

      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);

      // Lets teardown (destroy / re-render mid-drag) end this session even
      // though these listeners live on `document`, outside `_domListeners`.
      this._activePointerCleanup = () => {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
      };
    });
  }

  // Swallows the single click event the browser synthesises after a
  // mouseup that follows a drag or resize. Registered on `document` in the
  // capture phase rather than on the bar node itself: after a cross-row
  // drag, the synthesised click's target is the nearest common ancestor of
  // the mousedown and mouseup targets, which is not the bar — so a listener
  // on the bar might never see the event at all. Intercepting at `document`
  // in capture guarantees it is stopped first, matching how snipsnap's
  // day_view.js does this (there, on `window`).
  //
  // The listener expires on its own via a zero-delay timeout even if no
  // click ever arrives (e.g. the mouseup happens outside the document, so
  // no click is synthesised at all) — otherwise it stays armed and silently
  // swallows the operator's next unrelated click anywhere on the page. The
  // timeout is safe because the click (when one occurs) is dispatched in
  // the same task as mouseup, so it always lands before the timeout fires.
  _suppressNextClick() {
    const suppress = (e) => {
      e.stopPropagation();
      e.preventDefault();
      cleanup();
    };
    const cleanup = () => {
      document.removeEventListener("click", suppress, true);
      clearTimeout(timer);
    };
    document.addEventListener("click", suppress, true);
    const timer = setTimeout(cleanup, 0);
  }

  _handleEventClick(event, item) {
    event.preventDefault();
    this.scheduler._emit("event-click", { id: item.id, kind: item.kind });
  }

  // ---- helpers ----

  // Shifts both ends by whole days, preserving each one's time-of-day.
  _shift(item, dayDelta, resourceId) {
    return {
      start: this._addDays(item.start, dayDelta),
      end: this._addDays(item.end, dayDelta),
      resourceId
    };
  }

  _addDays(isoString, days) {
    const [datePart, timePart] = String(isoString).split("T");
    const d = parseLocalDate(datePart);
    d.setDate(d.getDate() + days);
    return timePart ? `${formatDateString(d)}T${timePart}` : formatDateString(d);
  }

  _on(target, event, handler) {
    target.addEventListener(event, handler);
    this._domListeners.push({ target, event, handler });
  }

  _tearDownDomListeners() {
    if (this._activePointerCleanup) {
      this._activePointerCleanup();
      this._activePointerCleanup = null;
    }
    this._domListeners.forEach(({ target, event, handler }) =>
      target.removeEventListener(event, handler)
    );
    this._domListeners = [];
  }
}
